import { IconWithText } from 'common/icon/icon-with-text.js';
import { LinkFactoryOnClick } from 'common/link/link-on-click.js';
import { LinkFactoryReactRouter } from 'common/link/link-react-router.js';
import * as React from 'react';
import { ReactNode } from 'react';
import { useAppDispatch } from 'redux-state/configure-store.js';
import { REDUX_SEARCH_CLOSE_OVERLAY } from 'redux-state/search/reducer.js';
import { ISearchResult, ISearchResultsGrouped } from 'redux-state/search/types.js';
import { ILocationSummary } from 'types/markerTypesV1/marker-v1-view-type.js';
import { trackNavigateEvent } from 'utilities/posthog-tracking';
import { getWindow } from 'utilities/window.js';
interface IProps {
  favourites?: Array<ILocationSummary>;
  handleSearchClick?: (result: ISearchResult) => void;
  inputValue?: string;
  recentSearches?: Array<ISearchResult>;
  searchResults?: ISearchResultsGrouped;
  title?: string;
}

// Search results with Towns & Cities `type` should appear at the top of the list, this constant is used to check
// that this is the first type we iterate through when displaying the results (if there are results with this type).
const FIRST_TYPE_FOR_SEARCH_RESULTS = 'Towns & Cities';

export const SearchContentResults = ({
  favourites,
  handleSearchClick,
  inputValue,
  recentSearches,
  title,
  searchResults,
}: IProps) => {
  const appDispatch = useAppDispatch();
  let listItems: ReactNode[] = [];

  if (recentSearches) {
    listItems = recentSearches.map((recentSearch, idx) => (
      <li className="SearchOverlay-list-item" key={idx}>
        <LinkFactoryReactRouter 
          onClick={
            () => {
              trackNavigateEvent('search_bar', 'recents_link');
              appDispatch(REDUX_SEARCH_CLOSE_OVERLAY());
            }
          }
          href={recentSearch.url}
        >
          <span className="u-textBold">{recentSearch.title}&#44;</span>
          <span className="u-textGrey u-pL-xs">{recentSearch.type}</span>
        </LinkFactoryReactRouter>
      </li>
    ));
  }

  if (favourites) {
    listItems = favourites.map(({ label, link }, idx) => {
      if (!link) {
        return null;
      }

      return (
        <li className="SearchOverlay-list-item" key={idx}>
          <LinkFactoryReactRouter
            onClick={
              () => {
                trackNavigateEvent('search_bar', 'favourites_link');
                appDispatch(REDUX_SEARCH_CLOSE_OVERLAY())
              }
            }
            href={link.url}
          >
            <IconWithText icon="favourite-solid" classes="IconWithText--vCenter IconWithText--small">
              <span className="u-textBold">{label}</span>
            </IconWithText>
          </LinkFactoryReactRouter>
        </li>
      );
    });
  }

  if (searchResults && handleSearchClick) {
    if (!searchResults.length) {
      return (
        <p className="u-mT-0">
          No results found for <strong>{inputValue}</strong>
        </p>
      );
    }

    let locationTypes = Object.keys(searchResults.groupedResults);
    const priorityTypeIndex = locationTypes.indexOf(FIRST_TYPE_FOR_SEARCH_RESULTS);

    // Puts Towns & Cities typed results at the beginning of the list.
    if (priorityTypeIndex !== -1) {
      locationTypes.splice(priorityTypeIndex, 1);

      locationTypes = [FIRST_TYPE_FOR_SEARCH_RESULTS, ...locationTypes];
    }

    listItems = locationTypes.map((type, locationIndex) => {
      const results = searchResults.groupedResults[type];
      const list = results.map((result, index) => (
        <li className="SearchOverlay-list-item" key={index}>
          <LinkFactoryOnClick
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              trackNavigateEvent('search_bar', 'search_result_link');
              handleSearchClick(result);
            }}
            onMouseDown={(a: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
              if (a.button === 1) {
                getWindow()?.open(result.url, '_blank');
              }
            }}
          >
            <span className="u-textBold">
              {result.title}
              {result.region ? ',' : null}
            </span>
            {result.region && <span className="u-textGrey u-pL-xs">{result.region}</span>}
          </LinkFactoryOnClick>
        </li>
      ));

      return (
        <React.Fragment key={locationIndex}>
          <h2 className="h2 u-mT-0 u-pB-sm u-bB-1">{type}</h2>
          <ul className="SearchOverlay-list u-mB-md">{list}</ul>
        </React.Fragment>
      );
    });
  }

  if (!listItems.length) {
    return null;
  }

  return (
    <div className="u-pB-lg">
      {title && <h3 className="h3 u-mT-0 u-pB-sm u-bB-1">{title}</h3>}
      <ul className="SearchOverlay-list u-mB-md">{listItems}</ul>
    </div>
  );
};
